/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IAccount, IClientSession, ISessionProvider } from "@adapptio/lib-core";
import { createEventEmitter, emitEvent, offEvent, onEvent } from "@adapptio/lib-shared";
import { EnvironmentService } from "../environment/EnvironmentService";

/**
 * Provides session and user data and handles reload
 */
export class EditorSessionProvider implements ISessionProvider {
	/** Emitted when session data has changed */
	public onChange = createEventEmitter<void>();

	private handleSessionChange: () => void;

	/**
	 * Constructor
	 */
	public constructor(private envSvc: EnvironmentService) {
		this.handleSessionChange = () => {
			emitEvent(this.onChange);
		};

		onEvent(this.envSvc.onSessionChange, this.handleSessionChange);
	}

	/**
	 * Returns session object
	 */
	public getSession(): IClientSession {
		return this.envSvc.getSession();
	}

	/**
	 * Returns user object
	 */
	public getIdentity(): IAccount {
		return this.envSvc.getIdentity();
	}

	/**
	 * Reloads session
	 */
	public async reload(reloadUserAccount?: boolean): Promise<void> {
		await this.envSvc.reloadSession(reloadUserAccount);
	}

	/**
	 * Disposes the provider
	 */
	public dispose(): void {
		offEvent(this.envSvc.onSessionChange, this.handleSessionChange);
	}
}
